import { IMessagePart } from '../types';

// The label line as the writer prints it: plain "Filters Applied:", a heading or a bold label, nothing else on the line.
const LABEL_LINE_REGEX = /^\s*(?:#{1,6}\s*)?(?:\*\*|__)?\s*Filters Applied\s*:?\s*(?:\*\*|__)?\s*:?\s*$/i;
// The label with its content on the same line ("Filters Applied: All available history"); a colon is required, so a
// sentence that merely starts with the words is not taken for the label.
const LABEL_INLINE_REGEX =
    /^\s*(?:#{1,6}\s*)?(?:\*\*|__)?\s*Filters Applied\s*(?::\s*(?:\*\*|__)?|(?:\*\*|__)\s*:)\s*(\S.*)$/i;
const LIST_ITEM_REGEX = /^\s*(?:[-*+]|\d+[.)])\s+\S/;
// A report's name over its own lines, as a composed answer groups them (ComposedAnswerWriter::filtersBlock).
const GROUP_LINE_REGEX = /^\s*(?:\*\*[^*]+\*\*|__[^_]+__)\s*$/;
// A table, a heading, a fence or a widget marker starts the next part of the answer even without a blank line.
const NEXT_PART_REGEX = /^\s*(?:\||#{1,6}\s|```|~~~|%BEGIN_)/;

export interface FiltersAppliedCut {
    body: string;
    // The block's lines without its label, as markdown; empty while only the label has streamed in.
    filters: string;
}

/**
 * The last "Filters Applied" block of an answer's text, cut out of it. The block is the label line and the lines that
 * follow it up to a blank line. A blank line does not end it between the label and its first line, nor between the
 * report groups of a block that opens with one; so a list or a titled list the answer itself writes after a block of
 * plain lines is never taken for filters.
 */
export function cutFiltersApplied(text: string): FiltersAppliedCut | null {
    const lines = text.split(/\r?\n/);
    const groupAt = (i: number) =>
        GROUP_LINE_REGEX.test(lines[i]) && i + 1 < lines.length && LIST_ITEM_REGEX.test(lines[i + 1]);
    let start = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
        if (LABEL_LINE_REGEX.test(lines[i]) || LABEL_INLINE_REGEX.test(lines[i])) {
            start = i;
            break;
        }
    }
    if (start === -1) {
        return null;
    }

    const content: string[] = [];
    const inline = LABEL_LINE_REGEX.test(lines[start]) ? null : LABEL_INLINE_REGEX.exec(lines[start]);
    if (inline) {
        content.push(inline[1]);
    }

    let grouped = false;
    let end = start + 1;
    while (end < lines.length) {
        const line = lines[end];
        if (line.trim() === '') {
            let next = end + 1;
            while (next < lines.length && lines[next].trim() === '') {
                next++;
            }
            const continues =
                next < lines.length &&
                (content.length === 0 ? LIST_ITEM_REGEX.test(lines[next]) || groupAt(next) : grouped && groupAt(next));
            if (!continues) {
                break;
            }
            if (content.length > 0) {
                content.push('');
            }
            end = next;
            continue;
        }
        if (NEXT_PART_REGEX.test(line)) {
            break;
        }
        if (content.length === 0 && groupAt(end)) {
            grouped = true;
        }
        content.push(line);
        end++;
    }

    return {
        body: [...lines.slice(0, start), ...lines.slice(end)].join('\n'),
        filters: content.join('\n').trim(),
    };
}

/** The message's last text part that holds a "Filters Applied" block, with the block cut out of it. */
export function findFiltersApplied(parts: IMessagePart[]): (FiltersAppliedCut & { part: IMessagePart }) | null {
    for (let i = parts.length - 1; i >= 0; i--) {
        const part = parts[i];
        if (part.type !== 'markdown' || !part.message) {
            continue;
        }
        const cut = cutFiltersApplied(part.message);
        if (cut) {
            return { ...cut, part };
        }
    }

    return null;
}
